import { useMemo, useRef, useEffect } from 'react';
import { ApolloProvider } from '@apollo/client';
import 'antd/dist/antd.min.css';

import TranslateIntlProvider from 'translations/TranslateIntlProvider';
import MessageContextProvider from 'utils/context/MessagesContext';
import { ReactKeycloakProvider, useKeycloak } from './auth/KeycloakProvider';
import BugsnagWrapper from './components/Bugsnag/BugsnagWrapper';
import HotjarProvider from './components/HotjarProvider';
import MaintenanceMode from './components/MaintenanceMode';
import { createApolloClient } from './graphql/client';
import keycloak from './keycloak';
import Fallback from './router/Fallback';
import MainRouter from './router/MainRouter';
import './styles/index.scss';

// Component that creates Apollo client with Keycloak context
const ApolloClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { keycloak: keycloakInstance } = useKeycloak();
  const keycloakRef = useRef(keycloakInstance);

  // Keep the ref updated
  useEffect(() => {
    keycloakRef.current = keycloakInstance;
  }, [keycloakInstance]);

  const apolloClient = useMemo(() => {
    const getKeycloakToken = () => {
      return keycloakRef.current?.token;
    };

    return createApolloClient(getKeycloakToken);
  }, []); // Create once and never recreate

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

const App: React.FC = () => {
  const maintenanceModeIsOn = false;
  const productionDomain = 'app.memoweb.be';

  return (
    <BugsnagWrapper>
      <ReactKeycloakProvider
        authClient={keycloak}
        autoRefreshToken
        LoadingComponent={<Fallback />}
        initOptions={{
          onLoad: 'login-required',
          // onLoad: 'check-sso',
          adapter: 'default',
          flow: 'implicit',
          checkLoginIframe: false,
        }}
        isLoadingCheck={(kc) => {
          // eslint-disable-next-line
          console;
          return !!kc.userInfo && !!kc.profile;
        }}
        onEvent={(event, error) => {
          if (event === 'onReady' && !error) {
            // eslint-disable-next-line
            //console.log('Keycloak is ready');
            keycloak.loadUserInfo();
            // eslint-disable-next-line
            //console.log('User info has been loaded');
            // eslint-disable-next-line
            //console.log('Keycloak event:', event);
            // eslint-disable-next-line
            //console.log('User is authenticated:', keycloak.authenticated);

            keycloak.loadUserProfile();
            // eslint-disable-next-line
            //console.log('User profile has been loaded');
            // eslint-disable-next-line
            //console.log('User profile:', keycloak.profile);
          }
        }}
      >
        <ApolloClientProvider>
          <TranslateIntlProvider>
            <HotjarProvider>
              {maintenanceModeIsOn && window.location.hostname.startsWith(productionDomain) ? (
                <MaintenanceMode />
              ) : (
                <MessageContextProvider>
                  <MainRouter />
                </MessageContextProvider>
              )}
            </HotjarProvider>
          </TranslateIntlProvider>
        </ApolloClientProvider>
      </ReactKeycloakProvider>
    </BugsnagWrapper>
  );
};

export default App;
